import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, FolderOpen } from "lucide-react";
import api, { formatErr } from "../../lib/api";

export default function AdmCategories({ basePath = "/categories", title = "Product Categories" }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ id: "", name: "", name_ar: "", icon: "" });
  const [editing, setEditing] = useState(null);
  const [counts, setCounts] = useState({});

  const refresh = async () => {
    try {
      const [cats, prods] = await Promise.all([
        api.get(basePath),
        api.get("/products?limit=500"),
      ]);
      setItems(cats.data);
      const map = {};
      const arr = prods.data.items || prods.data || [];
      arr.forEach((p) => { map[p.category] = (map[p.category] || 0) + 1; });
      setCounts(map);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.name) return toast.error("Name is required");
    try {
      await api.post(basePath, form);
      toast.success("Category created");
      setForm({ id: "", name: "", name_ar: "", icon: "" }); setCreating(false); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const save = async () => {
    try {
      await api.put(`${basePath}/${editing.id}`, { name: editing.name, name_ar: editing.name_ar, icon: editing.icon });
      toast.success("Updated"); setEditing(null); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (c) => {
    if ((counts[c.id] || 0) > 0) {
      toast.error(`In use by ${counts[c.id]} product(s) — reassign them first.`);
      return;
    }
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try {
      await api.delete(`${basePath}/${c.id}`);
      toast.success("Deleted"); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><div className="overline mb-2">Catalog</div><h1 className="font-display text-4xl text-white">{title}</h1></div>
        <button data-testid="cat-new" onClick={() => setCreating(!creating)} className="btn-primary"><Plus size={13}/> New Category</button>
      </div>

      {creating && (
        <form onSubmit={create} className="card-luxe p-5 grid md:grid-cols-4 gap-3">
          <input data-testid="cat-id" placeholder="Slug (auto if blank — e.g. luxury)" className="input-box" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })}/>
          <input data-testid="cat-name" required placeholder="Name (English) *" className="input-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
          <input data-testid="cat-name-ar" placeholder="Name (Arabic)" className="input-box" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl"/>
          <input placeholder="Icon name (optional)" className="input-box" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}/>
          <button data-testid="cat-create" className="btn-primary md:col-span-4 justify-center"><Save size={13}/> Create Category</button>
        </form>
      )}

      <div className="card-luxe overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-luxe text-white/40">
            <tr className="border-b border-white/5">
              <th className="text-left p-4">Slug</th><th className="text-left">Name</th><th className="text-left">Arabic</th><th className="text-left">Products</th><th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`cat-row-${c.id}`}>
                <td className="p-4 font-mono text-xs text-white/60">{c.id}</td>
                <td className="text-white">{c.name}</td>
                <td className="text-white/70" dir="rtl">{c.name_ar || "—"}</td>
                <td className="text-copper">{counts[c.id] || 0}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={`cat-edit-${c.id}`} onClick={() => setEditing({ ...c })} className="btn-ghost text-xs"><Pencil size={13}/></button>
                    <button data-testid={`cat-del-${c.id}`} onClick={() => remove(c)} className="text-xs px-2 py-1.5 text-red-400 hover:bg-red-500/10 border border-red-500/30"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-12 text-center text-white/40"><FolderOpen size={28} className="mx-auto mb-2 text-white/20"/>No categories yet</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div data-testid="cat-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setEditing(null)}>
          <div className="card-luxe p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div><div className="overline">Edit Category</div><div className="font-display text-2xl mt-1">{editing.name}</div></div>
              <button onClick={() => setEditing(null)} className="p-2 hover:text-copper"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <div><div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Name (English)</div><input data-testid="cat-edit-name" className="input-box" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}/></div>
              <div><div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Name (Arabic)</div><input data-testid="cat-edit-name-ar" className="input-box" dir="rtl" value={editing.name_ar || ""} onChange={(e) => setEditing({ ...editing, name_ar: e.target.value })}/></div>
              <div><div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Icon name</div><input className="input-box" value={editing.icon || ""} onChange={(e) => setEditing({ ...editing, icon: e.target.value })}/></div>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="cat-edit-save" onClick={save} className="btn-primary"><Save size={13}/> Save</button>
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
